import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { apiService, type Document as ApiDocument, type PDFSnippet, type SavedMessage, type DocumentFilters } from '../services/api';
import savedContentService, { type SavedContent } from '../services/savedContentService';
import { serializeSnippetToMarkdown } from '../utils/snippetMarkdown';

type DriveSnippet = PDFSnippet & {
  markdown?: string;
  front_matter?: Record<string, any>;
  parsed_content?: string;
};

interface UseDriveDataProps {
  documents?: ApiDocument[];
  debouncedSearchTerm?: string;
  searchInContent?: boolean;
  advancedFilters?: DocumentFilters;
}

// ============================================================================
// CONSTANTS
// ============================================================================
const POLLING_INTERVAL_MS = 2000;
const MAX_CONSECUTIVE_ERRORS = 3;
const ERROR_COOLDOWN_MS = 10000; // 10 segundos de cooldown después de errores

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Verifica si un documento está en procesamiento
 */
const isDocumentProcessing = (doc: ApiDocument): boolean => {
  return (
    doc.processing_status === 'pending' ||
    doc.processing_status === 'processing' ||
    (doc.processing_status === 'completed' && !doc.processing_time_seconds)
  );
};

/**
 * Helper para ejecutar una función de carga con protección contra múltiples peticiones
 * y circuit breaker para evitar peticiones infinitas cuando el backend está caído
 */
const createExecuteWithLoading = (
  consecutiveErrorsRef: React.MutableRefObject<number>,
  lastErrorTimeRef: React.MutableRefObject<number>
) => {
  return async <T,>(
    isLoadingRef: React.MutableRefObject<boolean>,
    setIsLoading: (value: boolean) => void,
    loadFn: () => Promise<T>,
    onSuccess: (data: T) => void,
    onError?: (error: unknown) => void
  ): Promise<void> => {
    if (isLoadingRef.current) return;

    // Circuit breaker: si hay demasiados errores consecutivos, esperar antes de intentar de nuevo
    const timeSinceLastError = Date.now() - lastErrorTimeRef.current;
    if (consecutiveErrorsRef.current >= MAX_CONSECUTIVE_ERRORS && timeSinceLastError < ERROR_COOLDOWN_MS) {
      // Silenciar - no hacer petición durante el cooldown
      return;
    }

    isLoadingRef.current = true;
    setIsLoading(true);

    try {
      const data = await loadFn();
      // Éxito: resetear contador de errores
      consecutiveErrorsRef.current = 0;
      onSuccess(data);
    } catch (error) {
      // Incrementar contador de errores y actualizar tiempo
      consecutiveErrorsRef.current += 1;
      lastErrorTimeRef.current = Date.now();
      onError?.(error);
    } finally {
      isLoadingRef.current = false;
      setIsLoading(false);
    }
  };
};

// ============================================================================
// MAIN HOOK
// ============================================================================

export const useDriveData = ({
  documents = [],
  debouncedSearchTerm = '',
  searchInContent = true,
  advancedFilters = {},
}: UseDriveDataProps) => {
  // ============================================================================
  // STATE - Single source of truth
  // ============================================================================
  const [privateDocuments, setPrivateDocuments] = useState<ApiDocument[]>([]);
  const [sharedDocuments, setSharedDocuments] = useState<ApiDocument[]>([]);
  const [savedMessages, setSavedMessages] = useState<SavedMessage[]>([]);
  const [snippets, setSnippets] = useState<DriveSnippet[]>([]);
  const [studioResults, setStudioResults] = useState<SavedContent[]>([]);

  const [isLoadingPrivate, setIsLoadingPrivate] = useState(false);
  const [isLoadingShared, setIsLoadingShared] = useState(false);
  const [isLoadingSaved, setIsLoadingSaved] = useState(false);

  // ============================================================================
  // REFS - Para evitar peticiones simultáneas y polling
  // ============================================================================
  const isLoadingPrivateRef = useRef(false);
  const isLoadingSharedRef = useRef(false);
  const isLoadingSavedRef = useRef(false);
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Circuit breaker: evitar peticiones si hay demasiados errores consecutivos
  const consecutiveErrorsRef = useRef(0);
  const lastErrorTimeRef = useRef<number>(0);

  // Refs para valores actuales en polling (evita dependencias innecesarias)
  const debouncedSearchTermRef = useRef(debouncedSearchTerm);
  const searchInContentRef = useRef(searchInContent);
  const advancedFiltersRef = useRef(advancedFilters);
  const documentsRef = useRef(documents);

  // Crear función executeWithLoading con circuit breaker (una sola vez)
  const executeWithLoading = useMemo(
    () => createExecuteWithLoading(consecutiveErrorsRef, lastErrorTimeRef),
    [] // Sin dependencias - función estable
  );

  // Mantener refs actualizados
  useEffect(() => {
    debouncedSearchTermRef.current = debouncedSearchTerm;
  }, [debouncedSearchTerm]);

  useEffect(() => {
    searchInContentRef.current = searchInContent;
  }, [searchInContent]);

  useEffect(() => {
    advancedFiltersRef.current = advancedFilters;
  }, [advancedFilters]);

  useEffect(() => {
    documentsRef.current = documents;
  }, [documents]);

  // ============================================================================
  // LOAD FUNCTIONS - Factorizadas y simplificadas
  // ============================================================================

  const loadPrivateDocuments = useCallback(async () => {
    await executeWithLoading(
      isLoadingPrivateRef,
      setIsLoadingPrivate,
      async () => {
        const trimmedSearch = debouncedSearchTermRef.current?.trim();
        return await apiService.getDocuments(
          trimmedSearch ? { search: trimmedSearch, search_in_content: searchInContentRef.current } : undefined
        );
      },
      (response) => {
        if (response.success) {
          const privateDocs = (response.documents || []).filter(doc => doc.scope !== 'shared');
          setPrivateDocuments(privateDocs);
        }
      },
      (error) => {
        console.error('Error loading private documents:', error);
        setPrivateDocuments(documentsRef.current);
      }
    );
  }, []); // Sin dependencias - usa refs

  const loadSharedDocuments = useCallback(async () => {
    await executeWithLoading(
      isLoadingSharedRef,
      setIsLoadingShared,
      async () => {
        const trimmedSearch = debouncedSearchTermRef.current?.trim();
        return await apiService.getSharedDocuments({
          ...advancedFiltersRef.current,
          search: trimmedSearch || undefined,
          search_in_content: searchInContentRef.current
        });
      },
      (response) => {
        if (response.success) {
          setSharedDocuments(response.documents || []);
        }
      },
      (error: any) => {
        if (error?.name !== 'ConnectionError' && error?.name !== 'NetworkError') {
          console.error('Error loading shared documents:', error);
        }
      }
    );
  }, []); // Sin dependencias - usa refs

  const loadSavedData = useCallback(async () => {
    await executeWithLoading(
      isLoadingSavedRef,
      setIsLoadingSaved,
      async () => {
        // Cargar cada recurso de forma independiente para que un error no afecte a los demás
        // Usar Promise.allSettled para manejar errores de forma resiliente
        const [messagesResult, snippetsResult, studioResult] = await Promise.allSettled([
          apiService.getSavedMessages().catch((error: unknown) => {
            // Solo loguear errores que no sean de red (network errors son esperados si el backend no está disponible)
            const isNetworkError =
              error instanceof TypeError ||
              (error instanceof Error && (
                error.name === 'TypeError' ||
                error.message.includes('Failed to fetch') ||
                error.message.includes('NetworkError') ||
                error.message.includes('ERR_')
              ));

            if (!isNetworkError) {
              console.error('Error loading saved messages:', error);
            }
            return [];
          }),
          apiService.getPDFSnippets({ include_image: true }).catch((error: unknown) => {
            // Solo loguear errores que no sean de red
            const isNetworkError =
              error instanceof TypeError ||
              (error instanceof Error && (
                error.name === 'TypeError' ||
                error.message.includes('Failed to fetch') ||
                error.message.includes('NetworkError') ||
                error.message.includes('ERR_')
              ));

            if (!isNetworkError) {
              console.error('Error loading snippets:', error);
            }
            return [];
          }),
          savedContentService.getSavedContent({}).catch((error: unknown) => {
            // savedContentService ya maneja errores de red internamente, solo loguear otros errores
            const isNetworkError =
              error instanceof TypeError ||
              (error instanceof Error && (
                error.name === 'TypeError' ||
                error.message.includes('Failed to fetch') ||
                error.message.includes('NetworkError') ||
                error.message.includes('ERR_')
              ));

            if (!isNetworkError) {
              console.error('Error loading saved Studio results:', error);
            }
            return { content: [] } as { content: SavedContent[] };
          })
        ]);

        // Extraer datos de forma segura, manejando tanto fulfilled como rejected
        const messagesData =
          messagesResult.status === 'fulfilled'
            ? (Array.isArray(messagesResult.value) ? messagesResult.value : [])
            : [];

        const snippetsData =
          snippetsResult.status === 'fulfilled'
            ? (Array.isArray(snippetsResult.value) ? snippetsResult.value : [])
            : [];

        const studioContent =
          studioResult.status === 'fulfilled'
            ? (studioResult.value && typeof studioResult.value === 'object' && 'content' in studioResult.value
                ? studioResult.value
                : { content: [] })
            : { content: [] };

        return { messagesData, snippetsData, studioContent };
      },
      ({ messagesData, snippetsData, studioContent }) => {
        setSavedMessages(messagesData || []);

        const snippetList = (snippetsData || []).map(snippet => {
          const { markdown, metadata, content } = serializeSnippetToMarkdown(snippet as any);
          return {
            ...snippet,
            markdown,
            front_matter: metadata,
            parsed_content: content,
          } as DriveSnippet;
        });

        setSnippets(snippetList);
        setStudioResults(studioContent?.content || []);
      },
      (error: unknown) => {
        // Solo loguear errores que no sean de red
        const isNetworkError =
          error instanceof TypeError ||
          (error instanceof Error && (
            error.name === 'TypeError' ||
            error.message.includes('Failed to fetch') ||
            error.message.includes('NetworkError') ||
            error.message.includes('ERR_')
          ));

        if (!isNetworkError) {
          console.error('Error loading saved data:', error);
        }
        // No fallar completamente - establecer valores por defecto
        setSavedMessages([]);
        setSnippets([]);
        setStudioResults([]);
      }
    );
  }, []);

  // ============================================================================
  // EFFECTS - Carga inicial y cuando cambian parámetros
  // ============================================================================

  // Refs para rastrear valores anteriores y evitar loops
  const prevSearchTermRef = useRef<string>('');
  const prevSearchInContentRef = useRef<boolean>(true);
  const prevAdvancedFiltersKeyRef = useRef<string>('{}');

  // Refs para rastrear si ya se cargó inicialmente (evitar fetch en cada remount)
  const hasInitiallyLoadedPrivateRef = useRef(false);
  const hasInitiallyLoadedSharedRef = useRef(false);
  const hasInitiallyLoadedSavedDataRef = useRef(false);

  // Serializar advancedFilters para comparación estable
  const advancedFiltersKey = useMemo(() => JSON.stringify(advancedFilters || {}), [advancedFilters]);

  // Cargar documentos privados: SOLO cuando cambian parámetros (no en mount)
  useEffect(() => {
    const searchTermChanged = prevSearchTermRef.current !== debouncedSearchTerm;
    const searchInContentChanged = prevSearchInContentRef.current !== searchInContent;
    const isFirstRender = !hasInitiallyLoadedPrivateRef.current;

    // En el primer render, solo actualizar refs, NO cargar
    if (isFirstRender) {
      prevSearchTermRef.current = debouncedSearchTerm;
      prevSearchInContentRef.current = searchInContent;
      hasInitiallyLoadedPrivateRef.current = true;
      // Solo cargar si no tenemos datos (primer mount ever)
      if (privateDocuments.length === 0) {
        loadPrivateDocuments();
      }
      return;
    }

    // En renders subsecuentes, solo cargar si cambian parámetros
    if (searchTermChanged || searchInContentChanged) {
      prevSearchTermRef.current = debouncedSearchTerm;
      prevSearchInContentRef.current = searchInContent;
      loadPrivateDocuments();
    }
  }, [debouncedSearchTerm, searchInContent, privateDocuments.length]); // Incluir length para check

  // Cargar documentos compartidos: SOLO cuando cambian parámetros (no en mount)
  useEffect(() => {
    const searchTermChanged = prevSearchTermRef.current !== debouncedSearchTerm;
    const searchInContentChanged = prevSearchInContentRef.current !== searchInContent;
    const filtersChanged = prevAdvancedFiltersKeyRef.current !== advancedFiltersKey;
    const isFirstRender = !hasInitiallyLoadedSharedRef.current;

    // En el primer render, solo actualizar refs, NO cargar
    if (isFirstRender) {
      prevSearchTermRef.current = debouncedSearchTerm;
      prevSearchInContentRef.current = searchInContent;
      prevAdvancedFiltersKeyRef.current = advancedFiltersKey;
      hasInitiallyLoadedSharedRef.current = true;
      // Solo cargar si no tenemos datos (primer mount ever)
      if (sharedDocuments.length === 0) {
        loadSharedDocuments();
      }
      return;
    }

    // En renders subsecuentes, solo cargar si cambian parámetros
    if (searchTermChanged || searchInContentChanged || filtersChanged) {
      prevSearchTermRef.current = debouncedSearchTerm;
      prevSearchInContentRef.current = searchInContent;
      prevAdvancedFiltersKeyRef.current = advancedFiltersKey;
      loadSharedDocuments();
    }
  }, [debouncedSearchTerm, searchInContent, advancedFiltersKey, sharedDocuments.length]); // Incluir length

  // Cargar datos guardados: SOLO si no tenemos datos (evitar reload en remounts)
  useEffect(() => {
    if (!hasInitiallyLoadedSavedDataRef.current) {
      hasInitiallyLoadedSavedDataRef.current = true;
      // Solo cargar si no tenemos datos
      if (savedMessages.length === 0 && snippets.length === 0 && studioResults.length === 0) {
        loadSavedData();
      }
    }
  }, [savedMessages.length, snippets.length, studioResults.length]); // Check data existence

  // ============================================================================
  // POLLING - DESHABILITADO (no necesario)
  // ============================================================================

  // Polling deshabilitado - no es necesario para este caso de uso
  // Si en el futuro necesitas actualizar documentos en procesamiento,
  // considera usar WebSockets o refetch manual con un botón de refresh

  // Limpiar polling interval al desmontar (cleanup)
  useEffect(() => {
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    };
  }, []);

  // ============================================================================
  // NOTA: Código de polling comentado por si se necesita en el futuro
  // ============================================================================
  /*
  const hasProcessingDocuments = useMemo(() => {
    return privateDocuments.some(isDocumentProcessing) ||
           sharedDocuments.some(isDocumentProcessing);
  }, [privateDocuments, sharedDocuments]);

  useEffect(() => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }

    if (!hasProcessingDocuments) {
      return;
    }

    const pollDocuments = async () => {
      if (debouncedSearchTermRef.current?.trim() || isLoadingPrivateRef.current || isLoadingSharedRef.current) {
        return;
      }

      const timeSinceLastError = Date.now() - lastErrorTimeRef.current;
      if (consecutiveErrorsRef.current >= MAX_CONSECUTIVE_ERRORS && timeSinceLastError < ERROR_COOLDOWN_MS) {
        return;
      }

      try {
        const [privateResponse, sharedResponse] = await Promise.all([
          apiService.getDocuments().catch(() => ({ success: false, documents: [] })),
          apiService.getSharedDocuments({ search_in_content: searchInContentRef.current }).catch(() => ({ success: false, documents: [] }))
        ]);

        if (!privateResponse.success && !sharedResponse.success) {
          consecutiveErrorsRef.current += 1;
          lastErrorTimeRef.current = Date.now();
        } else {
          consecutiveErrorsRef.current = 0;
        }

        if (privateResponse.success) {
          const privateDocs = (privateResponse.documents || []).filter(doc => doc.scope !== 'shared');
          setPrivateDocuments(privateDocs);
        }

        if (sharedResponse.success) {
          setSharedDocuments(sharedResponse.documents || []);
        }
      } catch (error) {
        consecutiveErrorsRef.current += 1;
        lastErrorTimeRef.current = Date.now();
      }
    };

    pollingIntervalRef.current = setInterval(pollDocuments, POLLING_INTERVAL_MS);

    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    };
  }, [hasProcessingDocuments]);
  */

  // ============================================================================
  // UTILITY FUNCTIONS
  // ============================================================================

  const updateDocumentStatus = useCallback((documentId: string, status: 'pending' | 'processing' | 'completed' | 'failed') => {
    setPrivateDocuments(prev => prev.map(doc =>
      doc.id === documentId ? { ...doc, processing_status: status } : doc
    ));
    setSharedDocuments(prev => prev.map(doc =>
      doc.id === documentId ? { ...doc, processing_status: status } : doc
    ));
  }, []);

  // ============================================================================
  // RETURN - API pública del hook
  // ============================================================================
  return {
    // Data
    privateDocuments,
    sharedDocuments,
    savedMessages,
    snippets,
    studioResults,

    // Loading states
    isLoadingPrivate,
    isLoadingShared,
    isLoadingSaved,

    // Load functions
    loadPrivateDocuments,
    loadSharedDocuments,
    loadSavedData,

    // Setters (para actualizaciones externas)
    setPrivateDocuments,
    setSharedDocuments,
    setSavedMessages,
    setSnippets,
    setStudioResults,

    // Utility
    updateDocumentStatus,
  };
};
